import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import TrustBadges from '../components/TrustBadges';
import ShilajitSection from '../components/ShilajitSection';
import ProductCard from '../components/ProductCard';
import EmailDiscountSignup from '../components/EmailDiscountSignup';
import DiscountModal from '../components/DiscountModal';
import { Product } from '../types/product';
import { fetchProducts } from '../utils/products';

const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const products = await fetchProducts();
        setFeaturedProducts(products.slice(0, 3));
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products');
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, []);

  useEffect(() => {
    const hasSeenModal = localStorage.getItem('hasSeenDiscountModal');
    const lastModalShown = localStorage.getItem('lastDiscountModalShown');
    const now = Date.now();
    const oneDayInMs = 24 * 60 * 60 * 1000;
    if (!hasSeenModal || (lastModalShown && now - parseInt(lastModalShown) > oneDayInMs)) {
      const timer = setTimeout(() => {
        setShowDiscountModal(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseModal = () => {
    setShowDiscountModal(false);
    localStorage.setItem('hasSeenDiscountModal', 'true');
    localStorage.setItem('lastDiscountModalShown', Date.now().toString());
  };

  return (
    <div>
      {/* Discount Modal */}
      <DiscountModal 
        isOpen={showDiscountModal} 
        onClose={handleCloseModal} 
      />

      <Hero
        title="Discover the Power of Pure Himalayan Shilajit"
        subtitle="Experience nature's most potent source of vitality and wellness, carefully harvested from the heart of the Himalayas."
        ctaText1="Shop Now"
        ctaLink1="/products"
        ctaText2="Learn More"
        ctaLink2="/about"
      />

      <TrustBadges />

      <ShilajitSection />

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2b603e] to-[#1a3d25] mb-2">Why Choose Our Shilajit?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our Shilajit is sourced from the highest peaks of the Himalayas and undergoes rigorous purification to ensure the highest quality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Cognitive Function</h3>
              <p className="text-gray-600">
                Traditionally used in natural wellness routines, this resin is appreciated for its long-standing place in practices focused on balance and clarity.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Energy & Stamina</h3>
              <p className="text-gray-600">
              Traditionally valued for supporting natural energy levels and endurance as part of holistic wellness practices.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Vitality</h3>
              <p className="text-gray-600">
                Traditionally valued for its role in holistic wellness, this resin is used as part of daily routines that may support natural blood flow and overall vitality.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Resilience</h3>
              <p className="text-gray-600">
                Rich in naturally occurring compounds, this resin is traditionally used to support the body's overall balance and resilience as part of a healthy lifestyle.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section 
        className="py-24 relative overflow-hidden bg-cover bg-center md:bg-fixed" 
        style={{ 
          backgroundImage: `url(${require('../assets/images/background.jpg')})`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#2b603e]/90 to-[#1a3d25]/90"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">EXPERIENCE THE POWER OF PURE SHILAJIT</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-white/90">
            Discover our premium collection of pure Himalayan Shilajit & Herbs.
          </p>
          <Link 
            to="/products" 
            className="bg-white hover:bg-gray-100 text-[#2b603e] font-bold py-4 px-8 rounded-full inline-block transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
            SHOP NOW
          </Link>
        </div>
      </section>


      {/* Featured Products Section */}
      <section className="py-24 bg-gradient-to-br from-[#2b603e]/5 to-[#2b603e]/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#2b603e] to-[#1a3d25] pb-2 border-b-4 border-[#2b603e] inline-block">
              OUR PRODUCTS
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Handpicked products that showcase the best of Asclepius Shilajit
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">Loading featured products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-xl text-red-600 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="bg-green-600 text-white px-6 py-3 rounded-full hover:bg-green-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {featuredProducts.map((product) => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    variant="featured" 
                  />
                ))}
              </div>

              <div className="text-center">
                <Link 
                  to="/products" 
                  className="inline-block bg-[#2b603e] hover:bg-[#1a3d25] text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105"
                >
                  View All Products
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Email Discount Signup Section */}
      <EmailDiscountSignup />

      {/* Contact Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#2b603e] mb-4">
              Connect With Us
            </h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Your wellness journey starts with a conversation. Reach out and let's explore how we can support your health goals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#2b603e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-8 w-8 text-[#2b603e]" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" 
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b603e] mb-2">Email</h3>
              <p className="text-gray-600">info@asclepius-shilajit.com</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#2b603e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-8 w-8 text-[#2b603e]" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" 
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b603e] mb-2">Phone</h3>
              <p className="text-gray-600">+31 657945978</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#2b603e]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-8 w-8 text-[#2b603e]" 
                  fill="currentColor" 
                  viewBox="0 0 32 32"
                >
                  <path d="M16 0C7.164 0 0 7.163 0 16c0 2.82.735 5.451 2.018 7.744L0 32l8.441-2.165A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.262 13.262 0 0 1-6.84-1.864l-.491-.294-5.002 1.282 1.313-4.872-.318-.502a13.231 13.231 0 0 1-2.036-7.078c0-7.346 5.98-13.333 13.333-13.333S29.333 8.654 29.333 16 23.354 29.333 16 29.333z"/>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#2b603e] mb-2">WhatsApp</h3>
              <p className="text-gray-600">+31 657945978</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/contact" 
              className="inline-block bg-[#2b603e] text-white hover:bg-[#1a3d25] font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;